import { CheckCircleFilled } from "@ant-design/icons"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../../utils/cn.ts"
import {
  BUILDER_SECTIONS,
  type BuilderSectionKey,
  sectionElementId,
  sectionHeadingId,
  sectionTitle
} from "../../helpers/section-completion"

export function CompletionState({ complete }: { complete: boolean }) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs font-medium",
        complete ? "text-primary" : "text-gray-500"
      )}
    >
      {complete ? (
        <CheckCircleFilled aria-hidden={"true"} />
      ) : (
        <span
          aria-hidden={"true"}
          className={"inline-block h-3 w-3 rounded-full border border-gray-400"}
        />
      )}
      {t(
        `settingsAutomation.builder.sections.${complete ? "complete" : "incomplete"}`
      )}
    </span>
  )
}

interface BuilderSectionProps {
  sectionKey: BuilderSectionKey
  complete: boolean
  children: ReactNode
}

// One of the builder's four numbered surfaces. The heading is focusable so the
// progress steps can move focus to it. The section spans the editor column;
// its body stops at a readable width.
export function BuilderSection({
  sectionKey,
  complete,
  children
}: BuilderSectionProps) {
  const { t } = useTranslation()
  const { number } = BUILDER_SECTIONS.find(
    (section) => section.key === sectionKey
  )!
  const title = sectionTitle(t, sectionKey)
  const headingId = sectionHeadingId(sectionKey)
  return (
    <section
      id={sectionElementId(sectionKey)}
      aria-labelledby={headingId}
      className={
        "scroll-mt-4 rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
      }
    >
      <div className={"mb-4 flex flex-wrap items-center justify-between gap-2"}>
        {/* The global h1-h3 rule centres headings. */}
        <h3
          id={headingId}
          tabIndex={-1}
          className={
            "m-0 flex items-center gap-2 text-start text-base font-semibold outline-none"
          }
        >
          <span
            aria-hidden={"true"}
            className={
              "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-bg text-xs font-semibold text-primary-strong"
            }
          >
            {number}
          </span>
          {title}
        </h3>
        <CompletionState complete={complete} />
      </div>
      {/* The page has no outer width cap, so the fields carry their own. */}
      <div className={"max-w-[56rem]"}>{children}</div>
    </section>
  )
}
